import { AvailabilityComponentCheckout } from "@components/click-and-collect/AvailabilityComponentCheckout";
import type {
    ProductVariant,
    ProductVariantAvailabilityWithChannel,
} from "@graphql/generated/components";
import { ClickAndCollect, CommonCMS } from "@lib/constants/contentful";
import { checkoutPage, shoppingBagPage, storeLocatorPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ui/components/content/text/Text";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
} from "@ui/components/disclosure/accordion/Accordion";
import { Spinner } from "@ui/components/feedback/spinner/Spinner";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { useRouter } from "next/router";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";
import { AvailabilityComponent } from "../click-and-collect/AvailabilityComponent";
import { Address } from "./Address";
import { accordion, loaderStyles } from "./StoreLocator.style";
import { AddressWithStocks } from "./address-with-stocks";
import type { StoreData } from "./controller";
import { Controller } from "./controller";
import { formatStreet } from "./normalize-store";
import { isEccoStore } from "./utils/isEccoStore";
import { storeTypeGuard } from "./utils/storeTypeGuard";
import { WalkInHours } from "./walk-in-hours";
import { humanizeDistance } from "./humanize-distance";
import { activeSiteConfig } from "@config/site/site-config";
import {
    STORE,
    STORE_LIST_LOADER,
    VIRTUAL_LIST_WRAPPER,
} from "@e2e/helpers/store-locator/constants/common";

interface StoreListProps {
    onClose?: () => void;
    hideLoader?: boolean;
    selectedVariant?: ProductVariant;
    storesWithStock?: ProductVariantAvailabilityWithChannel[] | StoreData[];
}

export const StoreList = ({
    hideLoader,
    onClose,
    selectedVariant,
    storesWithStock,
}: StoreListProps) => {
    const { get: getMicrocopy } = useMicrocopy();
    const { pathname, locale } = useRouter();
    const [storesWithoutVisible, setStoresWithoutVisible] = useState<StoreData[]>([]);
    const [storesPagination, setStoresPagination] = useState(1);
    const {
        selectedStoreId,
        setSelectedStoreId,
        setHighlightSelectedStore,
        storeDataIndex,
        visibleStores,
        searchResult,
        storeData,
        showPartnerStores,
        getShouldShowStore,
        isLoading,
        storeTypeFilter,
        clientLocation,
        isClickAndCollect,
        setStoresInList,
    } = useContext(Controller);

    const storeRef = useRef<HTMLDivElement>(null);

    const listRef = useRef<VirtuosoHandle | null>(null);

    const {
        geo: { imperialMetricSystem },
    } = activeSiteConfig;

    const storeWithStocksDictionary = useMemo(() => {
        const dictionary = {};
        storesWithStock?.forEach((item) => {
            dictionary[item?.supplyChannel?.[0]?.id ?? item?.channel?.id] = item;
        });

        return dictionary;
    }, [storesWithStock]);

    // Most of the logic of the storeLocator is based on storesVisible on the map
    // if visibleStores is empty and is mobile, set a state with stores only for this country
    useEffect(() => {
        if (visibleStores.length === 0) {
            const mergedStores = storeData.map((storeDataItem) => {
                const storeWithStocks =
                    storeWithStocksDictionary[storeDataItem.supplyChannel?.[0].id];
                if (storeWithStocks) {
                    return storeDataItem.storeId;
                }
                return storeWithStocks;
            });
            const filteredStores = mergedStores.filter((storeId) => storeId);

            setStoresWithoutVisible(filteredStores);
        }
    }, [storeData]);

    const onClickStore = (storeId: string) => () => {
        if (storeId === selectedStoreId) {
            setSelectedStoreId("");
            setHighlightSelectedStore(false);
            return;
        }
        setSelectedStoreId(storeId);
        setHighlightSelectedStore(true);
    };

    const data = useMemo(() => {
        const stores = visibleStores.length === 0 ? storesWithoutVisible : visibleStores;
        const isStoreData = storeTypeGuard(stores?.[0]);
        const filterCallback = isStoreData
            ? (store: StoreData) => getShouldShowStore(store.storeId)
            : (id: string) => getShouldShowStore(id);
        //@ts-ignore
        return stores.filter(filterCallback);
    }, [showPartnerStores, visibleStores.length]);

    const parseDistance = (distString: string | undefined): number => {
        if (!distString || distString === ">200 km" || distString === ">300 mi") return Infinity;
        // We are splitting the distance string into value and unit
        const [value, unit] = distString.includes("<")
            ? distString.replace("< ", "").split(" ")
            : distString.split(" ");
        const numValue = parseFloat(value);
        switch (unit) {
            case "m":
                return numValue;
            case "yd":
                return numValue * 0.9144; // Convert yards to meters
            case "km":
                return numValue * 1000;
            case "mi":
                return numValue * 1609.34; // Convert miles to meters
            default:
                return Infinity;
        }
    };

    const sortedStores = useMemo(() => {
        if (isClickAndCollect)
            return storeData.filter((store) => visibleStores.includes(store.storeId));
        const mapAndSortStores = (storeDataIndex, criteria) => {
            // We need to map stores to get the values like storeType and distance to sort them
            const mappedStores = (
                visibleStores.length === 0 ? storesWithoutVisible : visibleStores
            ).map((storeId) => {
                const store = storeDataIndex[storeId];
                const storeWithDistance = {
                    ...store,
                };

                if (clientLocation) {
                    storeWithDistance.distanceFromClient = humanizeDistance(
                        {
                            latitude: clientLocation?.lat,
                            longitude: clientLocation?.lng,
                        },
                        {
                            latitude: store?.latitude,
                            longitude: store?.longitude,
                        },
                        locale,
                        !imperialMetricSystem ? "metric" : "imperial"
                    );
                }
                return storeWithDistance;
            });

            // Based on the storeTypeFilter we will map the stores
            return mappedStores
                .filter((store) => getShouldShowStore(store.storeId))
                .sort((a, b) => {
                    if (criteria === "storeType") {
                        if (a?.storeTypeId === "ECCO" && b?.storeTypeId !== "ECCO") {
                            return -1;
                        }
                        if (a?.storeTypeId !== "ECCO" && b?.storeTypeId === "ECCO") {
                            return 1;
                        }
                        // If both stores are of the same type, sort by distance
                        const distA = parseDistance(a?.distanceFromClient);
                        const distB = parseDistance(b?.distanceFromClient);
                        if (distA === distB) {
                            return a?.name?.localeCompare(b.name);
                        }
                        return distA - distB;
                    } else if (criteria === "distance") {
                        // Parse the distance strings to numbers for comparison
                        const distA = parseDistance(a?.distanceFromClient);
                        const distB = parseDistance(b?.distanceFromClient);
                        if (distA === distB) {
                            // If distances are equal, sort alphabetically by name
                            return a?.name?.localeCompare(b?.name);
                        }
                        return distA - distB;
                    }
                    // Default case: sort alphabetically by name
                    return a?.name?.localeCompare(b?.name);
                });
        };

        return mapAndSortStores(storeDataIndex, storeTypeFilter.value).slice(
            0,
            storesPagination * 100
        );
    }, [data, storeDataIndex, storeTypeFilter, showPartnerStores, storesPagination]);

    useEffect(() => {
        setStoresInList(sortedStores.length);
    }, [sortedStores]);

    useEffect(() => {
        if (!data.length) return;
        const addressIndex = sortedStores.findIndex((v) => v.storeId === selectedStoreId);

        listRef.current?.scrollToIndex({ index: addressIndex });
    }, [selectedStoreId, data]);

    if (!hideLoader && isLoading) {
        return (
            <Box data-testid={STORE_LIST_LOADER} sx={loaderStyles}>
                <Spinner size="lg" />
            </Box>
        );
    }

    if (
        (searchResult?.length === 0 || data.length === 0) &&
        // if the search is not initialized or the number of stores is bigger than 100, we don't want to show stores neither the message
        searchResult !== undefined &&
        visibleStores.length >= 100
    ) {
        return (
            <Box pr={4}>
                <Text fontSize={"desktopBodyTextLarge"}>
                    {getMicrocopy(CommonCMS.page, ClickAndCollect.noStoresFound)}
                </Text>
            </Box>
        );
    }

    const generateLocalDate = (storeTimeZone: string): Date => {
        const oddTimeZone =
            storeTimeZone === "ALA" ||
            storeTimeZone === "MSTO" ||
            storeTimeZone === "MSTNO" ||
            storeTimeZone === "ESTNO" ||
            storeTimeZone === "AUSNSW" ||
            storeTimeZone === "AUSQLD" ||
            storeTimeZone === "AUSVIC" ||
            storeTimeZone === "AUSWA" ||
            storeTimeZone === "AUSACT" ||
            storeTimeZone === "AUSSA";

        const isValidTimeZone = (timeZone: string) => {
            try {
                Intl.DateTimeFormat(locale, { timeZone });
                return true;
            } catch (e) {
                return false;
            }
        };

        // As of now we have two timezones coming from SAP with wrong values
        // ALA and MSTO are not valid tz names, so we will map them manually, otherwise the formatter will break.
        const timeZonesFixed = {
            ALA: "America/Anchorage",
            MSTO: "America/Denver",
            MSTNO: "America/Phoenix",
            ESTNO: "America/Indiana/Indianapolis",
            AUSNSW: "Australia/Sydney",
            AUSQLD: "Australia/Brisbane",
            AUSVIC: "Australia/Melbourne",
            AUSWA: "Australia/Perth",
            AUSACT: "Australia/Sydney",
            AUSSA: "Australia/Adelaide",
        };

        let timeZone = oddTimeZone ? timeZonesFixed[storeTimeZone] : storeTimeZone;

        if (!isValidTimeZone(timeZone)) {
            timeZone = "UTC";
        }

        const formatterOptions: Intl.DateTimeFormatOptions = {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        };

        const date = new Date();

        const formatter = new Intl.DateTimeFormat(locale, formatterOptions);

        const parts = formatter.formatToParts(date);
        // We will build our date object based on the parts we got from the formatter to get types and methods from the Date object
        const dateParts: { [key: string]: number } = {};
        parts.forEach((part) => {
            if (["year", "month", "day", "hour", "minute", "second"].includes(part.type)) {
                dateParts[part.type] = parseInt(part.value, 10);
            }
        });
        dateParts.month -= 1;
        return new Date(
            dateParts.year,
            dateParts.month,
            dateParts.day,
            dateParts.hour,
            dateParts.minute,
            dateParts.second
        );
    };

    return (
        <Virtuoso
            style={{
                height: "100%",
            }}
            data-testid={VIRTUAL_LIST_WRAPPER}
            data-stores-count={sortedStores.length}
            data-ecco-stores-count={sortedStores.filter((store) => isEccoStore(store)).length}
            computeItemKey={(_, sortedStores) =>
                storeTypeGuard(sortedStores) ? sortedStores.storeId : sortedStores[_]?.storeId
            }
            data={sortedStores.slice(0, storesPagination * 50)}
            ref={listRef}
            itemContent={(_, sortedStore) => {
                const storeId = storeTypeGuard(sortedStore) ? sortedStore.storeId : sortedStore;
                const localDate = generateLocalDate(sortedStore?.timeZone);

                return (
                    <Accordion
                        sx={accordion.container}
                        ref={storeRef}
                        index={storeId === selectedStoreId ? 0 : -1}
                        allowToggle
                        reduceMotion
                        key={storeTypeFilter.value}
                    >
                        <AccordionItem
                            sx={{
                                ...accordion.item,
                                transitionProperty: "common",
                                transitionDuration: "normal",

                                _hover: {
                                    bg: !storeLocatorPage ? "blackAlpha.50" : "transparent",
                                },
                            }}
                            key={storeId}
                            id={storeId}
                        >
                            <AccordionButton
                                onClick={onClickStore(storeId)}
                                sx={{
                                    ...accordion.button,
                                    _hover: {
                                        bg: !storeLocatorPage ? "blackAlpha.50" : "transparent",
                                    },
                                }}
                                zIndex={9}
                                data-is-ecco-store={isEccoStore(sortedStore)}
                                data-testid={STORE}
                            >
                                {!selectedVariant ? (
                                    <Flex w="100%" position="relative">
                                        <Address
                                            isEccoStore={isEccoStore(sortedStore)}
                                            name={sortedStore?.name}
                                            street={formatStreet(sortedStore)}
                                            postalCode={sortedStore?.postalCode}
                                            city={sortedStore?.city}
                                            localDate={localDate}
                                            walkInHours={sortedStore?.walkInHours}
                                            latitude={sortedStore?.latitude}
                                            longitude={sortedStore?.longitude}
                                            isExpanded={selectedStoreId === storeId}
                                        />
                                        <AccordionIcon position={"absolute"} right={0} top={0} />
                                    </Flex>
                                ) : (
                                    <Flex w="100%" position="relative">
                                        <AddressWithStocks
                                            isEccoStore={isEccoStore(sortedStore)}
                                            name={sortedStore?.name}
                                            street={formatStreet(sortedStore)}
                                            postalCode={sortedStore?.postalCode}
                                            city={sortedStore?.city}
                                            localDate={localDate}
                                            walkInHours={sortedStore?.walkInHours}
                                            latitude={sortedStore?.latitude}
                                            longitude={sortedStore?.longitude}
                                            isExpanded={selectedStoreId === storeId}
                                        />
                                        <AccordionIcon position={"absolute"} right={0} top={0} />
                                    </Flex>
                                )}
                            </AccordionButton>
                            <AccordionPanel sx={accordion.panel}>
                                <WalkInHours
                                    localDate={localDate}
                                    walkInHours={sortedStore?.walkInHours}
                                    name={sortedStore?.name}
                                    street={formatStreet(sortedStore)}
                                    postalCode={sortedStore?.postalCode}
                                    city={sortedStore?.city}
                                    phone={sortedStore?.phone}
                                    latitude={sortedStore?.latitude}
                                    longitude={sortedStore?.longitude}
                                />
                            </AccordionPanel>
                            {selectedVariant &&
                            !Object.values(checkoutPage).find((path) => pathname.includes(path)) &&
                            !Object.values(shoppingBagPage).find((path) =>
                                pathname.includes(path)
                            ) ? (
                                <Box bg="transparent" pb={8} pt={0} px={6}>
                                    <AvailabilityComponent
                                        selectedVariant={selectedVariant}
                                        onClose={onClose}
                                        store={sortedStore}
                                    />
                                </Box>
                            ) : (
                                !Object.values(storeLocatorPage).find((path) =>
                                    pathname.includes(path)
                                ) && (
                                    <Box bg="transparent" pb={8} pt={0} px={6}>
                                        <AvailabilityComponentCheckout
                                            onClose={onClose}
                                            store={sortedStore}
                                        />
                                    </Box>
                                )
                            )}
                        </AccordionItem>
                    </Accordion>
                );
            }}
            atBottomStateChange={(isAtBottom) => {
                if (isAtBottom) {
                    setStoresPagination(storesPagination + 1);
                }
            }}
        />
    );
};
